import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "../../config/firebase";
import { getFirestore, doc, onSnapshot, getDoc } from "firebase/firestore";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import axios from "axios";

const ConnectionManager = () => {
    const [connectionCode, setConnectionCode] = useState("");
    const [inputCode, setInputCode] = useState("");
    const [connectedUser, setConnectedUser] = useState(null);
    const [error, setError] = useState("");
    const [countdown, setCountdown] = useState(null);
    const [showRelationshipModal, setShowRelationshipModal] = useState(false);
    const [relationshipStartDate, setRelationshipStartDate] = useState(null);
    const [relationshipName, setRelationshipName] = useState("");
    const [connectedUserId, setConnectedUserId] = useState(null);
    const [relationshipStatus, setRelationshipStatus] = useState({
        connected: false,
        partnerName: null,
        loading: true
    });
    const currentUser = auth.currentUser;
    const toast = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => {
        const fetchConnectionStatus = async () => {
            if (!currentUser) {
                setRelationshipStatus({ connected: false, partnerName: null, loading: false });
                return;
            }

            try {
                // Get user information from Firestore to check relationship status
                const userRef = doc(db, 'UserInformation', currentUser.uid);
                const userSnap = await getDoc(userRef);

                if (!userSnap.exists()) {
                    setRelationshipStatus({ connected: false, partnerName: null, loading: false });
                    return;
                }

                const userData = userSnap.data();

                if (!userData.inRelation || !userData.inRelationWith) {
                    setRelationshipStatus({ connected: false, partnerName: null, loading: false });
                    return;
                }

                // Get partner information
                const partnerRef = doc(db, 'UserInformation', userData.inRelationWith);
                const partnerSnap = await getDoc(partnerRef);

                if (!partnerSnap.exists()) {
                    setRelationshipStatus({ connected: false, partnerName: null, loading: false });
                    return;
                }

                const partnerData = partnerSnap.data();

                // Verify both users are connected to each other
                if (partnerData.inRelation && partnerData.inRelationWith === currentUser.uid) {
                    setRelationshipStatus({
                        connected: true,
                        partnerName: partnerData.displayName || partnerData.email || 'Partner',
                        partnerId: userData.inRelationWith,
                        loading: false
                    });

                    // Clear any generated code when connection is established
                    if (connectionCode) {
                        setConnectionCode("");
                        setCountdown(null);
                        if (timerRef.current) {
                            clearInterval(timerRef.current);
                        }
                    }
                } else {
                    setRelationshipStatus({ connected: false, partnerName: null, loading: false });
                }
            } catch (error) {
                console.error("Error fetching connection status:", error);
                setRelationshipStatus({ connected: false, partnerName: null, loading: false });
            }
        };

        fetchConnectionStatus();

        // Set up polling to check connection status every 10 seconds
        const intervalId = setInterval(fetchConnectionStatus, 10000);

        // Clean up the interval when component unmounts
        return () => {
            clearInterval(intervalId);
        };
    }, [currentUser, connectionCode]);
    // Generate a unique connection code valid for 2 minutes
    const generateCode = async () => {
        if (!currentUser) return;

        try {
            const response = await axios.post('https://api.frazza.online/connection/generate', {
                userId: currentUser.uid
            });

            if (response.data.success) {
                setConnectionCode(response.data.code);
                startCountdown(new Date(response.data.expiresAt));
            } else {
                console.error('Failed to generate connection code:', response.data.error);
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to generate connection code'
                });
            }
        } catch (error) {
            console.error('Error generating connection code:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to generate connection code'
            });
        }
    };

    // Start a countdown timer for code expiration
    const startCountdown = (expirationTime) => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        timerRef.current = setInterval(() => {
            const now = new Date();
            const timeLeft = Math.floor((expirationTime - now) / 1000);

            if (timeLeft <= 0) {
                clearInterval(timerRef.current);
                setConnectionCode("");
                setCountdown(null);
                toast.current.show({
                    severity: 'info',
                    summary: 'Code Expired',
                    detail: 'Your connection code has expired'
                });
            } else {
                const minutes = Math.floor(timeLeft / 60);
                const seconds = timeLeft % 60;
                setCountdown(`${minutes}:${seconds < 10 ? '0' : ''}${seconds}`);
            }
        }, 1000);
    };

    // Connect using someone else's code
    const connectWithCode = async () => {
        if (!currentUser || !inputCode) return;

        try {
            const response = await axios.post('https://api.frazza.online/connection/connect', {
                userId: currentUser.uid,
                code: inputCode
            });

            if (response.data.success) {
                const connectedUser = response.data.connectedUser;
                setConnectedUser(connectedUser);
                setConnectedUserId(connectedUser.uid);

                // Show toast notification
                toast.current.show({
                    severity: 'success',
                    summary: 'Connected!',
                    detail: `You are now in a relationship with ${connectedUser.name || 'your partner'}`
                });

                // Show relationship details modal
                setShowRelationshipModal(true);

                setInputCode("");
                setError("");

                // Update relationship status
                setRelationshipStatus({
                    connected: true,
                    partnerName: connectedUser.name || "your partner",
                    partnerId: connectedUser.uid,
                    loading: false
                });
            } else {
                setError(response.data.error || "Failed to connect");
            }
        } catch (err) {
            console.error("Error connecting:", err);
            if (err.response && err.response.data && err.response.data.error) {
                setError(err.response.data.error);
            } else {
                setError("Error connecting: " + err.message);
            }
        }
    };


    // Save relationship details
    const saveRelationshipDetails = async () => {
        if (!currentUser || !connectedUserId) return;

        try {
            const response = await axios.post('https://api.frazza.online/connection/relationship', {
                userId: currentUser.uid,
                partnerId: connectedUserId,
                startDate: relationshipStartDate,
                relationshipName: relationshipName
            });

            if (response.data.success) {
                // Close modal
                setShowRelationshipModal(false);

                toast.current.show({
                    severity: 'success',
                    summary: 'Relationship Details Saved',
                    detail: 'Your relationship details have been saved successfully'
                });
            } else {
                throw new Error(response.data.error || 'Failed to save relationship details');
            }
        } catch (error) {
            console.error("Error saving relationship details:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Could not save relationship details'
            });
        }
    };

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    // Relationship modal footer
    const relationshipModalFooter = (
        <>
            <Button label="Skip" icon="pi pi-times" onClick={() => setShowRelationshipModal(false)} className="p-button-text" />
            <Button label="Save" icon="pi pi-check" onClick={saveRelationshipDetails} />
        </>
    );

    return (
        <div className="connection-manager">
            <Toast ref={toast} />
            <h1>Connection Manager</h1>

            {/* Relationship Status Indicator */}
            <div className={`relationship-status ${relationshipStatus.connected ? 'connected' : 'not-connected'}`}>
                <div className="status-indicator"></div>
                <p>
                    {relationshipStatus.loading ? (
                        "Checking relationship status..."
                    ) : relationshipStatus.connected ? (
                        <>Currently in a relationship with <strong>{relationshipStatus.partnerName}</strong></>
                    ) : (
                        "Not currently in a relationship"
                    )}
                </p>
            </div>

            {/* Only show generation and connection if not already connected */}
            {!relationshipStatus.connected && (
                <>
                    <div className="generate-code">
                        <h3>Generate a Connection Code</h3>
                        <p>Share this code with someone you want to connect with:</p>
                        {connectionCode ? (
                            <div className="code-display">
                                <h4>{connectionCode}</h4>
                                {countdown && <p>Expires in: {countdown}</p>}
                            </div>
                        ) : (
                            <button onClick={generateCode}>Generate Code</button>
                        )}
                    </div>

                    <div className="use-code">
                        <h3>Use a Connection Code</h3>
                        <input
                            type="text"
                            value={inputCode}
                            onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                            placeholder="Enter code"
                        />
                        <button onClick={connectWithCode}>Connect</button>
                        {error && <p className="error">{error}</p>}
                    </div>
                </>
            )}
            {/* Relationship Details Modal */}
            <Dialog
                header="Relationship Details"
                visible={showRelationshipModal}
                className="frazza-dialog-sm"
                footer={relationshipModalFooter}
                onHide={() => setShowRelationshipModal(false)}
            >
                <div className="p-fluid">
                    <div className="frazza-form-group">
                        <label htmlFor="startDate" className="frazza-form-label">When did your relationship start?</label>
                        <Calendar
                            id="startDate"
                            value={relationshipStartDate}
                            onChange={(e) => setRelationshipStartDate(e.value)}
                            showIcon
                            dateFormat="dd/mm/yy"
                            maxDate={new Date()}
                            className="frazza-input"
                        />
                    </div>
                    <div className="frazza-form-group">
                        <label htmlFor="relationshipName" className="frazza-form-label">Relationship Name (optional)</label>
                        <InputText
                            id="relationshipName"
                            value={relationshipName}
                            onChange={(e) => setRelationshipName(e.target.value)}
                            placeholder="e.g., Our Love Story"
                            className="frazza-input"
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default ConnectionManager;
